import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import {
  Coins, Star, Gift, Activity, AlertCircle, AlertTriangle, TrendingUp, TrendingDown,
  FileText, Loader2, Sparkles, CreditCard, Settings2, Search, ShieldCheck, Download, Clock, Bell,
} from 'lucide-react';
import { fmtCr, formatMoney, getActionLabel, getActionIcon, isVisibleAction } from './billingHelpers';
import { ConsolidatedSpendAnalytics } from './ConsolidatedSpendAnalytics';
import { SpendControls } from './SpendControls';
import { CreditPoolCard } from './CreditPoolCard';

export const BillingCreditsTab = ({
  credits, currentPlan, costDashboard, usageHistory, usageHistoryLoading,
  creditPurchaseLoading, customCreditAmount, setCustomCreditAmount,
  autoTopup, setAutoTopup, autoTopupSaving,
  historyPeriod, setHistoryPeriod, historyCategory, setHistoryCategory,
  historyDirection = 'all', setHistoryDirection, historySearch = '', setHistorySearch,
  historyFrom = '', setHistoryFrom, historyTo = '', setHistoryTo,
  lowAlert = { enabled: false, threshold_credits: 500, email: '' }, setLowAlert, onSaveLowAlert, lowAlertSaving,
  onPurchaseCredits, onCustomCreditPurchase, onSaveAutoTopup, onLoadUsageHistory,
  onExportUsage, exportingUsage,
  creditSummary, gateway = 'stripe', currency = 'USD', gstRatePct = 18, usdInrRate = 0,
}) => {

  const balanceCr = Number(credits?.balance_credits ?? (credits ? fmtCr(credits.credit_balance_usd) : 0)) || 0;
  const dailyRateCr = Number(costDashboard?.daily_rate_credits || 0);
  const daysLeft = dailyRateCr > 0 ? Math.floor(balanceCr / dailyRateCr) : null;
  const daysTone = daysLeft == null ? 'text-muted-foreground' : daysLeft > 14 ? 'text-emerald-500' : daysLeft > 5 ? 'text-amber-500' : 'text-red-500';
  const lowBalance = (creditSummary?.credits_low ?? (balanceCr < 200));
  const cycleLabel = (() => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const opts = { month: 'short', day: 'numeric' };
    return `${first.toLocaleDateString('en-US', opts)} – ${d.toLocaleDateString('en-US', opts)}`;
  })();

  const matchesFilters = (tx) => {
    if (!isVisibleAction(tx.action)) return false;
    if (historyDirection === 'spend' && tx.is_credit) return false;
    if (historyDirection === 'added' && !tx.is_credit) return false;
    if (historySearch && historySearch.trim()) {
      const q = historySearch.trim().toLowerCase();
      const hay = `${tx.label || getActionLabel(tx.action)} ${tx.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const visibleDays = (usageHistory?.days || [])
    .map((day) => ({ ...day, _tx: day.transactions.filter(matchesFilters) }))
    .filter((day) => day._tx.length > 0);

  return (
    <div className="space-y-6">
      {gateway === 'razorpay' && (
        <Alert data-testid="razorpay-credits-note" className="border-indigo-500/30 bg-indigo-500/5">
          <CreditCard className="h-4 w-4 text-indigo-400" />
          <AlertDescription>
            Payments are processed securely in <span className="font-semibold">INR via Razorpay</span> (UPI, cards &amp; netbanking), inclusive of {gstRatePct}% GST. A GST tax invoice is issued automatically.
          </AlertDescription>
        </Alert>
      )}

      {/* Alerts */}
      {costDashboard?.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="credit-alerts">
          {costDashboard.alerts.map((alert, i) => (
            <Alert key={i} variant={alert.type === 'critical' ? 'destructive' : 'default'}>
              {alert.type === 'critical' ? <AlertCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              <AlertDescription>{alert.message}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* ===== 1. QUICK-BUY HERO — balance + packs, zero-scroll top-up ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        {/* Balance + forecast */}
        <Card data-testid="credit-balance-card" className="lg:col-span-5 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-6 flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Coins className="h-3.5 w-3.5 text-amber-500" />Available balance
              </div>
              <div className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-primary tabular-nums" data-testid="credit-balance-amount">
                {balanceCr.toLocaleString()} <span className="text-2xl font-bold text-primary/70">Cr</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">100 Cr = $1.00 · spend on AI, voice, data &amp; outreach</p>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {daysLeft != null && (
                <Badge variant="outline" className={`gap-1 ${daysTone} border-current/30`} data-testid="forecast-days-left">
                  <Clock className="h-3 w-3" />{daysLeft > 365 ? '365+' : daysLeft} days of credits left
                </Badge>
              )}
              {dailyRateCr > 0 && (
                <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid="forecast-burn-rate">
                  <TrendingDown className="h-3 w-3" />{Math.round(dailyRateCr).toLocaleString()} Cr/day
                </Badge>
              )}
            </div>
            {lowBalance && (
              <Alert variant="default" className="mt-4 border-amber-500/30 bg-amber-500/5" data-testid="low-balance-alert">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-300 text-sm">Low balance — top up to keep your AI running.</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Quick-buy packs */}
        <Card data-testid="buy-credits-card" className="lg:col-span-7">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-primary" />Buy credits</CardTitle>
            <CardDescription>Pick a pack or enter a custom amount — added instantly.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {credits?.packs_available?.map((pack) => {
                const isHighValue = pack.bonus_percent >= 20;
                return (
                  <div key={pack.pack_id}
                    className={`relative p-4 rounded-xl border-2 flex flex-col transition-all duration-200 hover:-translate-y-0.5 ${isHighValue ? 'border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-amber-600/5 hover:border-amber-500/70 hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]' : 'border-border bg-card hover:border-primary/50'}`}
                    data-testid={`credit-pack-${pack.pack_id}`}>
                    {pack.bonus_percent > 0 && (
                      <div className={`absolute -top-2.5 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold ${isHighValue ? 'bg-amber-500 text-black' : 'bg-emerald-500 text-white'}`}>
                        +{pack.bonus_percent}%{isHighValue ? ' Best value' : ' More'}
                      </div>
                    )}
                    <div className="text-2xl font-bold tabular-nums">{(pack.unit_credits || 0).toLocaleString()}</div>
                    <div className="text-[11px] text-muted-foreground -mt-0.5">credits</div>
                    {pack.bonus_percent > 0 && pack.base_credits && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        <span className="line-through">{pack.base_credits.toLocaleString()}</span>
                        <span className="ml-1 text-emerald-500 font-medium">→ {pack.unit_credits.toLocaleString()}</span>
                      </div>
                    )}
                    <div className={`mt-2 text-lg font-bold ${isHighValue ? 'text-amber-500' : 'text-primary'}`} data-testid={`credit-pack-price-${pack.pack_id}`}>
                      {formatMoney(pack.price_usd, { currency, usdInrRate })}{currency === 'INR' && <span className="text-[10px] font-normal text-muted-foreground"> +{gstRatePct}% GST</span>}
                    </div>
                    <Button className="w-full mt-3" size="sm" variant={isHighValue ? 'default' : 'outline'}
                      disabled={creditPurchaseLoading === pack.pack_id}
                      onClick={() => onPurchaseCredits(pack.pack_id)} data-testid={`buy-credits-${pack.pack_id}`}>
                      {creditPurchaseLoading === pack.pack_id ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />…</> : 'Buy'}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-end gap-3 pt-3 border-t border-border/50">
              <div className="flex-1">
                <label className="text-xs font-medium mb-1 block text-muted-foreground">Custom amount (USD){currency === 'INR' ? ' — charged in ₹' : ''}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input type="number" min={1} max={500} step={1} placeholder="Enter amount" className="pl-7"
                    value={customCreditAmount || ''} onChange={(e) => setCustomCreditAmount(parseFloat(e.target.value) || 0)}
                    data-testid="custom-credit-input" />
                </div>
                {currency === 'INR' && customCreditAmount > 0 && usdInrRate ? (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="custom-credit-inr-hint">
                    ≈ ₹{Math.round(customCreditAmount * usdInrRate * (1 + gstRatePct / 100)).toLocaleString('en-IN')} incl. {gstRatePct}% GST
                  </p>
                ) : customCreditAmount > 0 ? (
                  <p className="text-[11px] text-muted-foreground mt-1">= {(customCreditAmount * 100).toLocaleString()} Cr</p>
                ) : null}
              </div>
              <Button disabled={!customCreditAmount || customCreditAmount < 1 || creditPurchaseLoading === 'custom'}
                onClick={onCustomCreditPurchase} data-testid="buy-custom-credits-btn">
                {creditPurchaseLoading === 'custom' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Buy {customCreditAmount ? `${(customCreditAmount * 100).toLocaleString()} Cr` : ''}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== 2. RECONCILED SOURCES — where the balance came from ===== */}
      {credits && (
        <Card data-testid="credit-breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 uppercase tracking-wider text-muted-foreground font-semibold">
              <Coins className="h-3.5 w-3.5" />Where your credits came from
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const b = credits.breakdown || {};
              const sources = [
                { key: 'plan_ai_credits', label: 'Plan AI Credits', icon: Star, tone: 'text-amber-500', bg: 'bg-amber-500/10', always: true, desc: 'Included with your plan' },
                { key: 'reveal_credits', label: 'Reveal Credits', icon: Search, tone: 'text-cyan-500', bg: 'bg-cyan-500/10', desc: 'Contact reveals (phone/email)' },
                { key: 'bonus_credits', label: 'Signup Bonus', icon: Gift, tone: 'text-emerald-500', bg: 'bg-emerald-500/10', desc: 'Free credits on signup' },
                { key: 'topup_credits', label: 'Top-up Credits', icon: Sparkles, tone: 'text-violet-500', bg: 'bg-violet-500/10', desc: 'One-time purchases' },
                { key: 'admin_grant_credits', label: 'Admin Grant', icon: ShieldCheck, tone: 'text-blue-500', bg: 'bg-blue-500/10', desc: 'Granted by Lockene support' },
                { key: 'refund_credits', label: 'Refunds', icon: Coins, tone: 'text-teal-500', bg: 'bg-teal-500/10', desc: 'Credited back to your balance' },
                { key: 'other_credits', label: 'Adjustments', icon: Coins, tone: 'text-zinc-400', bg: 'bg-zinc-500/10', desc: 'Other credit adjustments' },
              ];
              const visible = sources.filter(s => s.always || Math.round(Number(b[s.key] || 0)) !== 0);
              const usedCr = Number(b.used_credits || 0);
              return (
                <div data-testid="credit-breakdown">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {visible.map((s) => {
                      const Icon = s.icon;
                      const val = Math.round(Number(b[s.key] || 0));
                      return (
                        <div key={s.key} className="p-4 rounded-lg bg-muted/30 border border-border" data-testid={`breakdown-${s.key}`}>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <span className={`p-1 rounded ${s.bg}`}><Icon className={`h-3.5 w-3.5 ${s.tone}`} /></span>{s.label}
                          </div>
                          <div className={`text-xl font-semibold ${val < 0 ? 'text-red-400' : ''}`}>
                            {val > 0 ? '+' : ''}{val.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                        </div>
                      );
                    })}
                    <div className="p-4 rounded-lg bg-muted/30 border border-border" data-testid="breakdown-used_credits">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <span className="p-1 rounded bg-red-500/10"><Activity className="h-3.5 w-3.5 text-red-400" /></span>Used
                        <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto">Lifetime</Badge>
                      </div>
                      <div className="text-xl font-semibold text-red-400">−{usedCr.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span></div>
                      <p className="text-xs text-muted-foreground mt-0.5">Total ever spent</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3" data-testid="breakdown-reconcile-note">
                    All sources minus lifetime usage = <span className="font-semibold text-foreground">{Number(b.balance_credits ?? credits.balance_credits ?? 0).toLocaleString()} Cr</span> available.
                  </p>

                  {/* Renewal split */}
                  {!credits.allowance_is_one_time && (Number(credits.expires_credits || 0) > 0 || Number(credits.carryover_credits || 0) > 0) && (
                    <div data-testid="credit-renewal-split" className="mt-4 rounded-lg border border-border bg-muted/20 p-4">
                      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <TrendingUp className="h-3.5 w-3.5" /> What happens at renewal
                        {credits.days_until_expiry != null && (
                          <Badge variant="outline" className="ml-1 text-[10px]" data-testid="renewal-days-badge">
                            in {credits.days_until_expiry} day{credits.days_until_expiry === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="renewal-carryover">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <span className="p-1 rounded bg-emerald-500/10"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /></span>Carries over
                          </div>
                          <div className="text-xl font-semibold text-emerald-500">{Number(credits.carryover_credits || 0).toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span></div>
                          <p className="text-xs text-muted-foreground mt-0.5">Your purchased top-ups never expire</p>
                        </div>
                        <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="renewal-expires">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <span className="p-1 rounded bg-amber-500/10"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>Expires if unused
                          </div>
                          <div className="text-xl font-semibold text-amber-500">{Number(credits.expires_credits || 0).toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Cr</span></div>
                          <p className="text-xs text-muted-foreground mt-0.5">Plan credits are use-it-or-lose-it</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* WA auto-refunds saved */}
                  {(creditSummary?.wa_failed_refunds?.count > 0) && (
                    <div className="mt-4 flex items-center gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3" data-testid="wa-refunds-saved">
                      <span className="p-1.5 rounded bg-emerald-500/10"><ShieldCheck className="h-4 w-4 text-emerald-500" /></span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-emerald-500">{Number(creditSummary.wa_failed_refunds.credits || 0).toLocaleString()} Cr saved from failed deliveries</div>
                        <p className="text-xs text-muted-foreground">Auto-refunded across {creditSummary.wa_failed_refunds.count} WhatsApp message{creditSummary.wa_failed_refunds.count === 1 ? '' : 's'} Meta billed but couldn&apos;t deliver — we protect your spend automatically.</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ===== 3. CONSOLIDATED SPEND ANALYTICS (merged 4-in-1) ===== */}
      <ConsolidatedSpendAnalytics costDashboard={costDashboard} usageHistory={usageHistory} creditSummary={creditSummary} cycleLabel={cycleLabel} />

      {/* ===== 4. USAGE HISTORY with advanced filters ===== */}
      <Card data-testid="usage-history-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-400" />Usage history</CardTitle>
              <CardDescription>Every credit transaction, newest first.</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={() => onExportUsage?.(historyPeriod, historyCategory, historyFrom, historyTo)} disabled={exportingUsage} data-testid="export-usage-csv-btn">
              {exportingUsage ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Export CSV
            </Button>
          </div>
          {/* Filter bar */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={historySearch} onChange={(e) => setHistorySearch?.(e.target.value)}
                placeholder="Search feature or description…" className="pl-8 h-9" data-testid="history-search-input" />
            </div>
            <select value={(historyFrom || historyTo) ? 'custom' : historyPeriod}
              onChange={(e) => { setHistoryPeriod(e.target.value); setHistoryFrom?.(''); setHistoryTo?.(''); onLoadUsageHistory(e.target.value, historyCategory); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="history-period-filter">
              <option value="today">Today</option>
              <option value="week">Last week</option>
              <option value="month">This month</option>
              <option value="all">All time</option>
              {(historyFrom || historyTo) && <option value="custom" disabled>Custom range</option>}
            </select>
            <div className="flex items-center gap-1" data-testid="history-date-range">
              <input type="date" value={historyFrom} max={historyTo || undefined}
                onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* noop */ } }}
                onChange={(e) => { setHistoryFrom?.(e.target.value); onLoadUsageHistory(historyPeriod, historyCategory, e.target.value, historyTo); }}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm dark:[color-scheme:dark] cursor-pointer" data-testid="history-from-date" title="From date" />
              <span className="text-xs text-muted-foreground">–</span>
              <input type="date" value={historyTo} min={historyFrom || undefined}
                onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* noop */ } }}
                onChange={(e) => { setHistoryTo?.(e.target.value); onLoadUsageHistory(historyPeriod, historyCategory, historyFrom, e.target.value); }}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm dark:[color-scheme:dark] cursor-pointer" data-testid="history-to-date" title="To date" />
              {(historyFrom || historyTo) && (
                <button onClick={() => { setHistoryFrom?.(''); setHistoryTo?.(''); onLoadUsageHistory(historyPeriod, historyCategory); }}
                  className="text-xs text-muted-foreground hover:text-foreground px-1" data-testid="history-clear-dates" title="Clear dates">✕</button>
              )}
            </div>
            <select value={historyCategory}
              onChange={(e) => { setHistoryCategory(e.target.value); onLoadUsageHistory(historyPeriod, e.target.value, historyFrom, historyTo); }}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="history-category-filter">
              <option value="all">All categories</option>
              <option value="ai">AI Brain</option>
              <option value="prospecting">Prospecting &amp; Data</option>
              <option value="outreach">Outreach</option>
              <option value="voice">Voice Calls</option>
              <option value="purchases">Purchases</option>
              <option value="adjustments">Adjustments &amp; Refunds</option>
            </select>
            <select value={historyDirection} onChange={(e) => setHistoryDirection?.(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="history-direction-filter">
              <option value="all">All directions</option>
              <option value="spend">Spent (−)</option>
              <option value="added">Added (+)</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {usageHistoryLoading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading usage history…</p>
            </div>
          ) : visibleDays.length > 0 ? (
            <div className="space-y-4">
              {visibleDays.map((day) => {
                const dateObj = new Date(day.date + 'T00:00:00');
                const dateLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
                return (
                  <div key={day.date} className="border border-border/50 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border/50">
                      <span className="text-xs font-bold tracking-wider text-muted-foreground">{dateLabel}</span>
                      <div className="flex items-center gap-4 text-xs">
                        {day.credits_added > 0 && <span className="text-muted-foreground">Added: <span className="font-semibold text-green-500">+{day.credits_added.toLocaleString()}</span></span>}
                        <span className="text-muted-foreground">Used: <span className="font-semibold text-foreground">{day.credits_used.toLocaleString()}</span></span>
                        <span className="text-muted-foreground">Balance: <span className="font-semibold text-foreground">{day.balance_end.toLocaleString()}</span></span>
                      </div>
                    </div>
                    <div className="divide-y divide-border/30">
                      {day._tx.map((tx, idx) => (
                        <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/10 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${getActionIcon(tx.action)?.replace('text-', 'bg-')}`} />
                            <div>
                              <p className="text-sm font-medium">{tx.label || getActionLabel(tx.action)}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[300px]">{tx.description}</p>
                            </div>
                          </div>
                          <span className={`font-mono text-sm font-semibold ${tx.is_credit ? 'text-green-500' : 'text-red-500'}`}>
                            {tx.is_credit ? '+' : '-'}{tx.credits.toLocaleString()} Cr
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {usageHistory?.capped && (
                <p className="text-xs text-muted-foreground text-center pt-1" data-testid="usage-capped-note">
                  Showing the most recent 200 entries. Use <span className="font-medium text-foreground">Export CSV</span> for the full ledger.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-testid="usage-history-empty">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>{(historySearch || historyDirection !== 'all') ? 'No transactions match your filters' : 'No usage found for this period'}</p>
              <p className="text-sm">Try a different time range, category or search.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== 5. CONTROLS & AUTOMATION ===== */}
      <div className="pt-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-4">
          <Settings2 className="h-4 w-4" />Controls &amp; automation
        </h3>
        <div className="space-y-6">
          <SpendControls />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Low-credit email alert */}
            <Card data-testid="low-credit-alert-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><Bell className="h-5 w-5 text-amber-500" />Low-credit alert</CardTitle>
                <CardDescription>Get an email before credits run out, so outreach never pauses unexpectedly</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                    <div>
                      <p className="font-medium">Email me when low</p>
                      <p className="text-sm text-muted-foreground">{lowAlert.email ? `Sent to ${lowAlert.email}` : 'Sent to your account email'}</p>
                    </div>
                    <Switch checked={lowAlert.enabled}
                      onCheckedChange={(checked) => setLowAlert(prev => ({ ...prev, enabled: checked }))}
                      data-testid="low-alert-toggle" />
                  </div>
                  {lowAlert.enabled && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Alert threshold (credits)</label>
                      <p className="text-xs text-muted-foreground">Email me once when my balance drops below this (resets daily)</p>
                      <Input type="number" min={50} step={50} value={lowAlert.threshold_credits}
                        onChange={(e) => setLowAlert(prev => ({ ...prev, threshold_credits: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) }))}
                        data-testid="low-alert-threshold" />
                    </div>
                  )}
                  <Button onClick={onSaveLowAlert} disabled={lowAlertSaving} data-testid="save-low-alert-btn">
                    {lowAlertSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save alert
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="auto-topup-card">
              <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Settings2 className="h-5 w-5 text-primary" />Auto top-up</CardTitle>
            <CardDescription>Automatically add credits when your balance drops below a threshold</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                <div>
                  <p className="font-medium">Enable auto top-up</p>
                  <p className="text-sm text-muted-foreground">Automatically recharge when balance is low</p>
                </div>
                <Switch checked={autoTopup.enabled}
                  onCheckedChange={(checked) => setAutoTopup(prev => ({ ...prev, enabled: checked }))}
                  data-testid="auto-topup-toggle" />
              </div>
              {autoTopup.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Threshold (USD)</label>
                    <p className="text-xs text-muted-foreground">Top-up when balance falls below ${autoTopup.threshold} ({fmtCr(autoTopup.threshold)} Cr)</p>
                    <Input type="number" min={1} step={1} value={autoTopup.threshold}
                      onChange={(e) => setAutoTopup(prev => ({ ...prev, threshold: parseFloat(e.target.value) || 5 }))}
                      data-testid="auto-topup-threshold" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Top-up amount (USD)</label>
                    <p className="text-xs text-muted-foreground">Adds {fmtCr(autoTopup.amount)} Cr each time</p>
                    <Input type="number" min={10} step={5} value={autoTopup.amount}
                      onChange={(e) => setAutoTopup(prev => ({ ...prev, amount: parseFloat(e.target.value) || 25 }))}
                      data-testid="auto-topup-amount" />
                  </div>
                </div>
              )}
              <Button onClick={onSaveAutoTopup} disabled={autoTopupSaving} data-testid="save-auto-topup-btn">
                {autoTopupSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save settings
              </Button>
            </div>
          </CardContent>
        </Card>
          </div>
          <CreditPoolCard />
        </div>
      </div>
    </div>
  );
};
